import { useMemo } from "react";
import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useApi, apiResolver } from "@/api/useApi";
import type {
  Snippet,
  SnippetDetail,
  CreateSnippetDto,
  CreateSnippetResponse,
  SaveSnippetVersionDto,
  VerifySnippetVersionDto,
  PresignAttachmentDto,
  PresignAttachmentResponse,
} from "./KnowledgeSnippet.model";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";

type SnippetApi = {
  getSnippets: (pathPrefix?: string) => Promise<Snippet[]>;
  getSnippetById: (snippetId: string) => Promise<SnippetDetail>;
  createSnippet: (data: CreateSnippetDto) => Promise<CreateSnippetResponse>;
  deleteSnippets: (snippetIds: string[]) => Promise<void>;
  updateSnippet: (snippetId: string, path: string) => Promise<void>;
  updateSnippetVersion: (
    snippetId: string,
    versionId: string,
    data: SaveSnippetVersionDto
  ) => Promise<void>;
  verifySnippetVersion: (
    snippetId: string,
    versionId: string,
    data: VerifySnippetVersionDto
  ) => Promise<void>;
  presignAttachment: (
    snippetId: string,
    data: PresignAttachmentDto
  ) => Promise<PresignAttachmentResponse>;
};

export function useSnippetApi(): SnippetApi {
  const { api } = useApi();
  const accessLabelsEnabled = useFlag("articleAccessLabels");

  return useMemo(
    () => ({
      getSnippets: async (pathPrefix?: string) => {
        const params = pathPrefix
          ? `?pathPrefix=${encodeURIComponent(pathPrefix)}`
          : "";
        return apiResolver<Snippet[]>(
          api.get(
            `/knowledge-base-handler/knowledge-management/snippets${params}`
          )
        );
      },
      getSnippetById: async (snippetId: string) =>
        apiResolver<SnippetDetail>(
          api.get(
            `/knowledge-base-handler/knowledge-management/snippets/${snippetId}${
              accessLabelsEnabled ? "?includeAccessLabels=true" : ""
            }`
          )
        ),
      createSnippet: async (data: CreateSnippetDto) =>
        apiResolver<CreateSnippetResponse>(
          api.post(
            "/knowledge-base-handler/knowledge-management/snippets",
            data
          )
        ),
      deleteSnippets: async (snippetIds: string[]) =>
        apiResolver<void>(
          api.post(
            "/knowledge-base-handler/knowledge-management/snippets/delete",
            { snippetIds }
          )
        ),
      updateSnippet: async (snippetId: string, path: string) =>
        apiResolver<void>(
          api.patch(
            `/knowledge-base-handler/knowledge-management/snippets/${snippetId}`,
            { path }
          )
        ),
      updateSnippetVersion: async (
        snippetId: string,
        versionId: string,
        data: SaveSnippetVersionDto
      ) =>
        apiResolver<void>(
          api.patch(
            `/knowledge-base-handler/knowledge-management/snippets/${snippetId}/versions/${versionId}`,
            data
          )
        ),
      verifySnippetVersion: async (
        snippetId: string,
        versionId: string,
        data: VerifySnippetVersionDto
      ) =>
        apiResolver<void>(
          api.post(
            `/knowledge-base-handler/knowledge-management/snippets/${snippetId}/versions/${versionId}/verify`,
            data
          )
        ),
      presignAttachment: async (
        snippetId: string,
        data: PresignAttachmentDto
      ) =>
        apiResolver<PresignAttachmentResponse>(
          api.post(
            `/knowledge-base-handler/knowledge-management/snippets/${snippetId}/attachments/presign`,
            data
          )
        ),
    }),
    [api, accessLabelsEnabled]
  );
}

export function useSnippets(pathPrefix?: string) {
  const { getSnippets } = useSnippetApi();

  return useQuery({
    queryKey: ["snippets", pathPrefix],
    queryFn: () => getSnippets(pathPrefix),
  });
}

export function useSnippetById(snippetId: string | null) {
  const { getSnippetById } = useSnippetApi();

  return useQuery({
    queryKey: ["snippet", snippetId],
    queryFn: () =>
      snippetId ? getSnippetById(snippetId) : Promise.reject("No snippet ID"),
    enabled: !!snippetId,
  });
}

/**
 * Hook to create a new snippet
 */
export function useCreateSnippet() {
  const queryClient = useQueryClient();
  const { createSnippet } = useSnippetApi();

  return useMutation({
    mutationFn: async (data: CreateSnippetDto) => {
      return createSnippet(data);
      // Mock implementation: return createMockSnippet(data.path, data.title || 'new');
    },
    onSuccess: () => {
      // Invalidate snippets query to refetch the list
      queryClient.invalidateQueries(["snippets"]);
    },
  });
}

/**
 * Hook to delete one or more snippets (batch delete)
 */
export function useDeleteSnippet() {
  const queryClient = useQueryClient();
  const { deleteSnippets } = useSnippetApi();

  return useMutation({
    mutationFn: async (snippetIds: string | string[]) => {
      const ids = Array.isArray(snippetIds) ? snippetIds : [snippetIds];
      return deleteSnippets(ids);
    },
    onSuccess: () => {
      // Invalidate snippets query to refetch the list
      queryClient.invalidateQueries(["snippets"]);
    },
  });
}

/**
 * Hook to update a snippet version draft (title and content)
 */
export function useUpdateSnippetVersion() {
  const queryClient = useQueryClient();
  const { updateSnippetVersion } = useSnippetApi();

  return useMutation({
    mutationFn: async ({
      snippetId,
      versionId,
      title,
      content,
    }: {
      snippetId: string;
      versionId: string;
      title: string;
      content: string;
    }) => {
      return updateSnippetVersion(snippetId, versionId, { title, content });
    },
    onSuccess: (_, variables) => {
      // Invalidate snippets list and the specific snippet to refetch data
      queryClient.invalidateQueries(["snippets"]);
      queryClient.invalidateQueries(["snippet", variables.snippetId]);
    },
  });
}

/**
 * Hook to verify a snippet version (publish it)
 */
export function useVerifySnippetVersion() {
  const queryClient = useQueryClient();
  const { verifySnippetVersion } = useSnippetApi();

  return useMutation({
    mutationFn: async ({
      snippetId,
      versionId,
      versionName,
      approvalNote,
      releaseAccessSnippets,
    }: {
      snippetId: string;
      versionId: string;
      versionName: string;
      approvalNote?: string;
      releaseAccessSnippets?: import("./KnowledgeSnippet.model").ReleaseAccessSnippet[];
    }) => {
      return verifySnippetVersion(snippetId, versionId, {
        versionName,
        approvalNote,
        releaseAccessSnippets,
      });
    },
    onSuccess: (_, variables) => {
      // Invalidate both the list and the specific snippet
      queryClient.invalidateQueries(["snippets"]);
      queryClient.invalidateQueries(["snippet", variables.snippetId]);
    },
  });
}

/**
 * Hook to upload an attachment (image or video) to a snippet
 * Returns the public URL of the uploaded file
 */
export function useUploadSnippetAttachment() {
  const { presignAttachment } = useSnippetApi();

  return useMutation({
    mutationFn: async ({
      snippetId,
      file,
      onProgress,
    }: {
      snippetId: string;
      file: File;
      onProgress?: (progress: number) => void;
    }) => {
      // Step 1: Get presigned URL
      const presignResponse = await presignAttachment(snippetId, {
        fileType: file.type,
        fileSize: file.size,
      });

      // Step 2: Upload file to S3 using presigned URL
      await axios.request({
        method: "PUT",
        url: presignResponse.uploadUrl,
        data: file,
        headers: {
          "Content-Type": file.type,
        },
        onUploadProgress: (event) => {
          if (!onProgress || event.total == null) {
            return;
          }
          const progress = Math.round((event.loaded / event.total) * 100);
          onProgress(progress);
        },
      });

      // Step 3: Return the public URL
      return presignResponse.publicUrl;
    },
  });
}

/**
 * Hook to update a collection name by updating all snippets in that collection
 */
export function useUpdateCollectionName() {
  const queryClient = useQueryClient();
  const { getSnippets, updateSnippet } = useSnippetApi();

  return useMutation({
    mutationFn: async ({
      collectionPath,
      newCollectionName,
    }: {
      collectionPath: string;
      newCollectionName: string;
    }) => {
      // Normalize the collection path (remove leading slash)
      const normalizedPath = collectionPath.startsWith("/")
        ? collectionPath.slice(1)
        : collectionPath;

      // Get all snippets in this collection and its subcollections
      const allSnippets = await getSnippets(normalizedPath);

      // Calculate the old and new collection paths
      const pathParts = normalizedPath.split("/");
      const oldCollectionName = pathParts[pathParts.length - 1];
      const parentPath = pathParts.slice(0, -1).join("/");
      const newCollectionPath = parentPath
        ? `${parentPath}/${newCollectionName}`
        : newCollectionName;

      // Update all snippets that are in this collection
      const updatePromises = allSnippets
        .map((snippet) => {
          const snippetPath = snippet.path.startsWith("/")
            ? snippet.path.slice(1)
            : snippet.path;

          // Only update snippets that are in this collection or its subcollections
          if (snippetPath !== normalizedPath && !snippetPath.startsWith(`${normalizedPath}/`)) {
            return null;
          }

          // Replace the old collection path with the new one
          let newPath: string;
          if (snippetPath === normalizedPath) {
            // This snippet is directly in the collection
            newPath = newCollectionPath;
          } else {
            // This snippet is in a subcollection
            const relativePath = snippetPath.slice(normalizedPath.length + 1);
            newPath = `${newCollectionPath}/${relativePath}`;
          }

          return updateSnippet(snippet.id, newPath);
        })
        .filter((promise): promise is Promise<void> => promise !== null);

      await Promise.all(updatePromises);
    },
    onSuccess: () => {
      // Invalidate snippets query to refetch the list
      queryClient.invalidateQueries(["snippets"]);
    },
  });
}
